import React, {
  useState,
  useCallback,
  useMemo
} from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Row,
  Col,
  Badge
} from 'reactstrap'
import {
  useLocation,
  useParams
} from 'react-router'
import { TitlePage } from 'components/atoms'
import { CardTextData } from 'components/molecules'
import {
  DataTable,
  DataTableStatic
} from 'components/organisms'
import {
  productCollectionAddItemTableColumns,
  productCollectionDeleteItemTableColumns,
  productCollectionCancelItemTableColumns
} from 'domains/product-collection/molecules'
import { useDropzone } from 'react-dropzone'
import LoaderButton from 'components/molecules/loader-button'
import ProductsApiV2 from 'api/v2/admins/products-api-v2'
import ProductCollectionsApiV2 from 'api/v2/admins/product-collections-api-v2'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import { WEB_URL_CONSTANTS } from 'constants/web-url-constants'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const TEMPLATE_LINK = 'https://drive.google.com/file/d/1pnLodVOKn3BuOwVBsScO3tQcUW9CN7h9/view'

const ProductCollectionDetailPage = ({ pageUtils }) => {
  const location = useLocation()
  const { id }   = useParams()

  const [ collectionItems, setCollectionItems ]                     = useState([])
  const [ collectionItemsPagination, setCollectionItemsPagination ] = useState({})

  const name         = location.state.name
  const url          = location.state.url
  const isStandalone = location.state.isStandalone ? 'Yes' : 'No'

  const [ products, setProducts ]                       = useState([])
  const [ paginationProducts, setPaginationProducts ]   = useState({})
  const [ isLoading, setIsLoading ]                     = useState(true)
  const [ isProductTableShown, setIsProductTableShown ] = useState(false)

  const [ selectedItems, setSelectedItems ] = useState([])

  const [ file, setFile ]                         = useState(new Blob)
  const [ isUploading, setIsUploading ]           = useState(false)
  const [ isSubmitNewItems, setIsSubmitNewItems ] = useState(false)

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    } else {
      pageUtils.setAlertMsg('Please upload file with .csv, .xls, or .xlsx format.', ALERT_TYPES.DANGER)
    }

  }, [ pageUtils ])

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    disabled : isUploading ,
    accept   : 'text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  /**
  * Upload Form Styling Begins
  */
  const baseStyle = useMemo(() => ({
    flex            : 1,
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'center',
    margin          : '20px',
    padding         : '20px',
    borderWidth     : 2,
    borderRadius    : 2,
    borderColor     : '#eeeeee',
    borderStyle     : 'dashed',
    backgroundColor : '#fafafa',
    color           : '#bdbdbd',
    outline         : 'none',
    transition      : 'border .24s ease-in-out'
  }), [])

  const activeStyle = useMemo(() => ({
    borderColor: '#2196f3'
  }), [])

  const acceptStyle = useMemo(() => ({
    borderColor: '#00e676'
  }), [])

  const rejectStyle = useMemo(() => ({
    borderColor: '#ff1744'
  }), [])

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isDragActive,
    isDragReject,
    isDragAccept,
    baseStyle,
    activeStyle,
    acceptStyle,
    rejectStyle
  ])

  const fileDropText = file.size === 0
    ?  'Drag and drop a CSV file here, or click to select files to upload product list'
    : file.name

  const uploadFile = (file) => {
    setIsUploading(true)
    ProductsApiV2.get_from_csv(file).then((response) => {
      setSelectedItems(response.data.products)
      pageUtils.setAlertMsg('CSV has been uploaded succesfully!', ALERT_TYPES.SUCCESS)
      pageUtils.setAlertMsg('Please check items in New Items table belows before Submit!', ALERT_TYPES.WARNING)
    }).catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsUploading(false))
  }

  const handleFetchCollectionItems = (tableState) => {
    ProductCollectionsApiV2.show({ id, tableState: tableState  })
      .then((response) => {
        const data = response.data.collection_items
        const meta = response.data.meta

        setCollectionItems(data)
        setCollectionItemsPagination(meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  const handleFetchProducts = (tableState) => {
    setIsLoading(true)
    ProductsApiV2.get({ tableState })
      .then((response) => {
        const data = response.data.products
        const meta = response.data.meta

        setProducts(data)
        setPaginationProducts(meta)
      })
      .catch((error) => alert(error))
      .finally( () => setIsLoading(false) )
  }

  const handleClickAddItem = useCallback((data) => {
    setSelectedItems([ ...selectedItems, data ])
  }, [ selectedItems ])

  const handleClickCancelItem = useCallback((data) => {
    const updateSelectedItems = selectedItems.filter((element) => element.sku !== data.sku)
    setSelectedItems(updateSelectedItems)
  }, [ selectedItems ])

  const handleClickDeleteItem = useCallback((data) => {
    const isRemoveAction = window.confirm('Are you sure to remove this item?')
    if (isRemoveAction) {
      const updatedCollectionItems = collectionItems.filter((element) => element.sku !== data.sku)
      setCollectionItems(updatedCollectionItems)

      ProductCollectionsApiV2.deleteItem({
        id,
        itemId: data.id
      })
        .then(() => {
          pageUtils.setAlertMsg('Successfully delete item!', ALERT_TYPES.SUCCESS)
          setCollectionItemsPagination((prevState) => ({
            ...prevState,
            total_count: prevState.total_count - 1
          }))
        })
        .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
    }
  }, [ collectionItems, id, pageUtils ])

  const handleSubmitNewItems = () => {
    const isSaveAction = window.confirm(`Are you sure to want to add ${selectedItems.length} products to this collection?`)

    if (isSaveAction) {
      setIsSubmitNewItems(true)
      const ids = selectedItems.map((item) => item.id)
      ProductCollectionsApiV2.addItems({
        items : ids,
        id    : id
      }).then(() => {
        pageUtils.setAlertMsg('Successfully add items!', ALERT_TYPES.SUCCESS)
        setSelectedItems([])
        setIsProductTableShown(false)
        handleFetchCollectionItems()
        if (file.size > 0) setFile(new Blob())
      }).catch((error) => pageUtils.setApiErrorMsg(error.response.data))
        .finally(() => setIsSubmitNewItems(false))
    }
  }

  //collection items table columns
  const columns = useMemo(() => productCollectionDeleteItemTableColumns({ handleClickDeleteItem }), [ handleClickDeleteItem ])

  //product table columns
  const addItemColumns = useMemo(() => productCollectionAddItemTableColumns({
    selectedItems,
    handleClickAddItem
  }), [ handleClickAddItem, selectedItems ], handleClickAddItem)

  //new items table columns
  const cancelItemColumns = useMemo(() => productCollectionCancelItemTableColumns({ handleClickCancelItem }),
    [ handleClickCancelItem ])

  const hiddenColumns = [
    'seller_name',
    'status',
    'price',
    'selling_price',
    'quantity'
  ]

  const NewItemsTable     = ({ selectedItems }) => {
    if (selectedItems.length === 0) return null

    return (
      <Card>
        <CardHeader>
          { 'New Items' } <Badge color="primary" >{ selectedItems.length }</Badge>
          <Button
            color="danger"
            className='float-right'
            size='sm'
            onClick={ () => setSelectedItems([]) }
          >
            Reset
          </Button>
        </CardHeader>
        <DataTableStatic
          data={ selectedItems }
          columns={ cancelItemColumns }
          hiddenColumns={ hiddenColumns }
        />
        <CardFooter>
          <LoaderButton
            color='success'
            className='float-right'
            isLoading={ isSubmitNewItems }
            onClick={ handleSubmitNewItems }
            text= 'SAVE'
          />
        </CardFooter>
      </Card>
    )
  }
  NewItemsTable.propTypes = { selectedItems: PropTypes.array }

  return (
    <>
      <TitlePage mainTitle='Product Collection Detail' subTitle={ `#${id}` } />
      <Row>
        <Col>
          <Card>
            <CardBody>
              <CardTextData label='Name' value={ name } />
              <CardTextData label='URL' value={ location.state.isStandalone ?
                <a
                  className='text-primary'
                  href={ `${WEB_URL_CONSTANTS.HOMEPAGE}/collections/${url}` }
                  target='_blank' rel='noopener noreferrer'
                >
                  { `/collections/${url}` }
                </a> : '-'
              }
              />
              <CardTextData label='Is Standalone Page?' value={ isStandalone } />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={ 4 }>
          <Card>
            <CardHeader>
              { 'Add Items by Uploading CSV' }
            </CardHeader>
            <div { ...getRootProps({ style }) }>
              <input { ...getInputProps() } />
              <p className='font-weight-bold'>
                { fileDropText }
              </p>
            </div>
            <CardBody>
              <h5>
                { 'Please follow the given CSV template ' }
                <a
                  className='text-primary'
                  href={ TEMPLATE_LINK }
                  target='_blank' rel='noopener noreferrer'
                >
                  { 'here' }
                </a>
              </h5>
              <LoaderButton
                className='float-right'
                color='primary'
                onClick={ () => uploadFile(file) }
                isLoading={ isUploading }
                disabled={ file.size === 0 }
                text = 'Upload'
              />
            </CardBody>
          </Card>
        </Col>
        <Col md={ 8 }>
          <Card>
            <CardHeader>
              { 'Add Item Manually' }
              <Button
                className='float-right'
                color='primary'
                size='sm'
                onClick={ () => setIsProductTableShown(!isProductTableShown) }
              >
                { 'Add New Item' }
              </Button>
            </CardHeader>
            {
              isProductTableShown ?
                <DataTable
                  data={ products }
                  pagination={ paginationProducts }
                  columns={ addItemColumns }
                  hiddenColumns={ hiddenColumns }
                  onFetchData={ handleFetchProducts }
                  isLoading={ isLoading }
                /> : null
            }
          </Card>
        </Col>
      </Row>

      <NewItemsTable selectedItems={ selectedItems } />
      <Card>
        <div className='d-flex'>
          <div className='flex-fill'>
            <CardHeader>
              { 'Current Items' } <Badge color="primary" >{ collectionItemsPagination.total_count }</Badge>
            </CardHeader>
            <DataTable
              data={ collectionItems }
              pagination={ collectionItemsPagination }
              columns={ columns }
              hiddenColumns={ hiddenColumns }
              onFetchData={ handleFetchCollectionItems }
            />
          </div>
        </div>
      </Card>
    </>
  )

}

ProductCollectionDetailPage.propTypes    = propTypes
ProductCollectionDetailPage.defaultProps = defaultProps

export default ProductCollectionDetailPage
